import { ArtworkImage } from './ArtworkImage';
import { getCardBackPath } from '../utils/cardBackArtwork';
import { useMonsterTokenStyle } from '../utils/clientSettings';
import { GateArtwork } from './GateArtwork';
import { AgentBadge } from './AgentBadge';
import { ExpeditionBadge } from './ExpeditionBadge';
import { locationCards, LocationCardsSection } from './LocationCards';
import { ClassicLocationDialog } from './ClassicLocationDialog';
import { AncientWhisperInfo, ExhibitEncounterCatalog } from './AncientWhisperInfo';
import { GameDialog } from './GameDialog';
import { InvestigatorInspector } from './InvestigatorInspector';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Location, Gate, UserAction, Investigator, Monster, GameState, EncounterCardInfo, RiftProgressEntry, FedRaid } from '../api';
import '../styles/Map.css';
import { Monsters, MonstersDetails } from './Monsters';
import { StackedItems } from './InvestigatorSheet';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { InvestigatorMarker } from './InvestigatorMarker';
import { GateMarker, getGateImagePath, getGateTooltip, getGateDestinations } from './GateMarker';
import { PlayerHeader } from './PlayersList';
import { RiftProgress } from './RiftProgress';
import { RiftMarker } from './RiftMarker';
import { observeElementResize } from './observeElementResize';

export type LocationPosition = { x: number; y: number, width?: number; height?: number };
export type LocationPositions = Record<string, LocationPosition>;

type LocationRiftProgressToken = {
  locationName: string;
  riftProgressEntry: RiftProgressEntry;
  occurrenceIndex: number;
  isOpen: boolean;
};

const DEFAULT_MAP_BASE_WIDTH = 900;
const DEFAULT_MAP_BASE_HEIGHT = 1600;
const DEFAULT_MAP_ASPECT_RATIO = '2553 / 3870';

const DEEP_ONE_RAISING_TRACK_POSITIONS: Record<number, { right: number, top: number }> = {
  0: {
    right: 7,
    top: 3
  },
  1: {
    right: 49,
    top: 39
  },
  2: {
    right: 82,
    top: 84
  },
  3: {
    right: 108,
    top: 133
  },
  4: {
    right: 126,
    top: 185
  },
  5: {
    right: 138,
    top: 234
  }
}

const normalizeOtherWorldName = (value: string) => value.replace(/\s+/g, '').toLowerCase();

const getGateDestinationInvestigatorNames = (gate: Gate, locations: Location[]) => {
  const otherWorldNames = getGateDestinations(gate).map(normalizeOtherWorldName);
  const names: string[] = [];
  const seenNames = new Set<string>();

  locations.forEach(location => {
    if (location.type !== 'OtherWorld') {
      return;
    }

    const match = location.name.match(/^(.+) (1|2)$/);
    if (!match || !otherWorldNames.includes(normalizeOtherWorldName(match[1]))) {
      return;
    }

    (location.investigatorNames ?? []).forEach(name => {
      if (!seenNames.has(name)) {
        seenNames.add(name);
        names.push(name);
      }
    });
  });

  return names;
};

interface LocationMonstersSectionProps {
  monsters?: Monster[];
}

const LocationMonstersSection: React.FC<LocationMonstersSectionProps> = ({ monsters }) => {
  if (!monsters?.length) {
    return <div className="detail-empty">No monsters</div>;
  }

  return (
    <div className="detail-monsters">
      <MonstersDetails monsters={monsters} stopPropagation />
    </div>
  );
};

interface MapProps {
  locations: Location[];
  positions?: LocationPositions;
  mapBaseWidth?: number;
  mapBaseHeight?: number;
  mapAspectRatio?: string;
  mapBackgroundImage?: string;
  showMapStats?: boolean;
  showTerrorTrack?: boolean;
  showDunwichTerrorTrack?: boolean;
  terrorLevel?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  dunwichHorrorTerrorTrack?: 0 | 1 | 2 | 3;
  activeRumorLocationName?: string | null;
  activeMythosLocationName?: string | null;
  activeProcessLocationName?: string | null;
  maximumMonstersInArkham?: number;
  outskirtsLimit?: number;
  maximumGates?: number;
  locationActions?: UserAction[];
  onExecuteAction?: (actionId: string) => void;
  loading?: boolean;
  investigatorsByName?: Record<string, Investigator>;
  highlightedInvestigators?: string[];
  currentInvestigator?: Investigator;
  viewerName?: string;
  gameState: GameState;
  deckInfo?: {
    commonItems?: { bottomCard?: any | null };
    uniqueItems?: { bottomCard?: any | null };
    spells?: { bottomCard?: any | null };
  };
  language?: string;
  gatesOpened?: number;
  seals?: number;
  investigatorPlayerMap?: Record<string, string>;
  riftProgress?: RiftProgressEntry[];
  deepOnesRisingTrack?: number;
  fedsRaidInnsmouthTrack?: FedRaid;
}

// Define default positions for each location on the map
export const DEFAULT_LOCATION_POSITIONS: LocationPositions = {
  'Abyss 1': { x: 813, y: 1334 },
  'Abyss 2': { x: 811, y: 1262 },
  'Administration Building': { x: 128, y: 975 },
  'AnotherDimension 1': { x: 811, y: 1539 },
  'AnotherDimension 2': { x: 811, y: 1454 },
  'Arkham Asylum': { x: 395, y: 56 },
  'Bank of Arkham': { x: 266, y: 55 },
  'Black Cave': { x: 644, y: 683 },
  'CityOfTheGreatRace 1': { x: 814, y: 1136 },
  'CityOfTheGreatRace 2': { x: 813, y: 1062 },
  'Curiositie Shoppe': { x: 58, y: 300 },
  'DownTown': { x: 384, y: 274, width: 110, height: 45 },
  'Dreamlands 1': { x: 811, y: 542 },
  'Dreamlands 2': { x: 809, y: 469 },
  'Easttown': { x: 471, y: 430, width: 120, height: 45 },
  'French Hill': { x: 463, y: 843, width: 110, height: 45 },
  'General Store': { x: 510, y: 680 },
  'Graveyard': { x: 646, y: 557 },
  'GreatHallOfCeleano 1': { x: 819, y: 743 },
  'GreatHallOfCeleano 2': { x: 820, y: 660 },
  'Hibb\'s Roadhouse': { x: 520, y: 220 },
  'Historical Society': { x: 458, y: 1334 },
  'Independence Square': { x: 518, y: 56 },
  'Jail Cell': { x: 580, y: 450, width: 50, height: 50 },
  'Library': { x: 243, y: 928 },
  'Lost in Time and Space': { x: 654, y: 1550 },
  'Ma\'s Boarding House': { x: 649, y: 1105 },
  'Merchant District': { x: 271, y: 564, width: 150, height: 45 },
  'Miskatonic University': { x: 277, y: 816, width: 135, height: 45 },
  'Newspaper': { x: 61, y: 174 },
  'Northside': { x: 212, y: 277, height: 45, width: 105 },
  'Outskirts': { x: 447, y: 1548 },
  'PlateauOfLeng 1': { x: 812, y: 345 },
  'PlateauOfLeng 2': { x: 813, y: 269 },
  'Police Station': { x: 649, y: 352 },
  'River Docks': { x: 56, y: 620 },
  'RiverTown': { x: 455, y: 564, width: 107, height: 45 },
  'Rlyeh 1': { x: 814, y: 148 },
  'Rlyeh 2': { x: 811, y: 71 },
  'Science Building': { x: 59, y: 802 },
  'Silver Twilight Lodge': { x: 535, y: 954 },
  'Sky': { x: 551, y: 1547 },
  'South Church': { x: 603, y: 1299 },
  'Southside': { x: 471, y: 1155, width: 105, height: 45 },
  'St. Mary\'s Hospital': { x: 58, y: 1149 },
  'The Unnamable': { x: 185, y: 681 },
  'The Witch House': { x: 649, y: 880 },
  'Train Station': { x: 142, y: 56 },
  'Unvisited Isle': { x: 61, y: 498 },
  'Uptown': { x: 302, y: 1156, height: 45 },
  'Velma\'s Diner': { x: 652, y: 220 },
  'Woods': { x: 311, y: 1337 },
  'Ye Olde Magick Shoppe': { x: 149, y: 1314 },
  'Yuggoth 1': { x: 816, y: 935 },
  'Yuggoth 2': { x: 813, y: 864 }
};

const TERROR_POSITIONS = {
  0: { bottom: 38, left: 55 },
  1: { bottom: 52, left: 95 },
  2: { bottom: 60, left: 142 },
  3: { bottom: 58, left: 187 },
  4: { bottom: 53, left: 235 },
  5: { bottom: 35, left: 275 },
  6: { bottom: 8, left: 74 },
  7: { bottom: 20, left: 121 },
  8: { bottom: 23, left: 165 },
  9: { bottom: 19, left: 211 },
  10: { bottom: 8, left: 255 },
}

const DUNWICH_TERROR_POSITIONS = {
  0: { top: 0, left: 0 },
  1: { top: 30, left: 418 },
  2: { top: 30, left: 492 },
  3: { top: 30, left: 563 },
}
const DUNWICH_HORROR_CARD_COUNT = 7;

export const Map: React.FC<MapProps> = React.memo(({
  locations,
  positions = DEFAULT_LOCATION_POSITIONS,
  mapBaseWidth = DEFAULT_MAP_BASE_WIDTH,
  mapBaseHeight = DEFAULT_MAP_BASE_HEIGHT,
  mapAspectRatio = DEFAULT_MAP_ASPECT_RATIO,
  mapBackgroundImage = '/en_gameboard.jpg',
  showMapStats = true,
  showTerrorTrack = true,
  showDunwichTerrorTrack = false,
  terrorLevel = 0,
  dunwichHorrorTerrorTrack = 0,
  activeRumorLocationName = null,
  activeMythosLocationName = null,
  activeProcessLocationName = null,
  maximumMonstersInArkham,
  maximumGates,
  outskirtsLimit,
  locationActions,
  onExecuteAction,
  loading,
  investigatorsByName,
  highlightedInvestigators = [],
  deckInfo,
  language,
  gatesOpened,
  seals,
  gameState,
  currentInvestigator,
  viewerName,
  investigatorPlayerMap = {},
  riftProgress,
  deepOnesRisingTrack,
  fedsRaidInnsmouthTrack,
}) => {
  const artworkStyle = useMonsterTokenStyle();
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [dunwichTerrorTooltipDisplayed, setDunwichTerrorTooltipDisplayed] = useState<boolean>(false);
  const [mapSize, setMapSize] = useState<{ width: number; height: number }>({
    width: mapBaseWidth,
    height: mapBaseHeight,
  });
  const monstersInArkham = locations
    .filter(
      l =>
        l.name !== 'Outskirts' &&
        !/ (1|2)$/.test(l.name)
    )
    .reduce((sum, loc) => sum.concat(loc.monsters || []), [] as Monster[]);
  const monstersInArkhamCount = monstersInArkham.filter((m) => m.type !== 'Spawn').length;
  const spawnMonstersInArkhamCount = monstersInArkham.filter((m) => m.type === 'Spawn');
  const monsterLimit = maximumMonstersInArkham ?? 0;
  const outskirtsMonsters = locations.find(l => l.name === 'Outskirts')?.monsters?.length ?? 0;
  const outskirtsMax = outskirtsLimit ?? 0;
  const [openInvestigatorName, setOpenInvestigatorName] = useState<string | null>(null);
  const openInvestigator = openInvestigatorName
    ? gameState.investigatorsByName?.[openInvestigatorName] ?? investigatorsByName?.[openInvestigatorName]
    : null;
  const otherWorldLookupLocations = gameState.locations?.length ? gameState.locations : locations;
  const encounterTopCardByLocation = useMemo<Record<string, EncounterCardInfo>>(() => {
    const byLocation: Record<string, EncounterCardInfo> = {};
    const encounterDecks = gameState.decks?.encounters;

    if (!encounterDecks) {
      return byLocation;
    }

    for (const [locationName, deck] of Object.entries(encounterDecks)) {
      if (!deck?.topCard) {
        continue;
      }

      if (!byLocation[locationName]) {
        byLocation[locationName] = deck.topCard;
      }

      if (deck.topCard.locationName && !byLocation[deck.topCard.locationName]) {
        byLocation[deck.topCard.locationName] = deck.topCard;
      }
    }

    return byLocation;
  }, [gameState.decks?.encounters]);
  const riftProgressTokensByLocation = useMemo<Record<string, LocationRiftProgressToken[]>>(() => {
    const byLocation: Record<string, LocationRiftProgressToken[]> = {};

    riftProgress?.forEach(riftProgressEntry => {
      riftProgressEntry.tracks?.forEach(track => {
        track.locations?.forEach(locationName => {
          if (!byLocation[locationName]) {
            byLocation[locationName] = [];
          }

          byLocation[locationName].push({
            locationName,
            riftProgressEntry,
            occurrenceIndex: byLocation[locationName].length,
            isOpen: riftProgressEntry.isOpen,
          });
        });
      });
    });

    return byLocation;
  }, [riftProgress]);

  useEffect(() => {
    const updateMapSize = () => {
      if (!mapRef.current) {
        return;
      }
      // Layout dimensions exclude the outer map camera transform.
      const computedStyle = window.getComputedStyle(mapRef.current);
      const rect = {
        width: parseFloat(computedStyle.width) || mapRef.current.offsetWidth,
        height: parseFloat(computedStyle.height) || mapRef.current.offsetHeight,
      };
      // jsdom and hidden standalone boards have no layout; retain their base geometry.
      if (!rect.width || !rect.height) return;
      // Board coordinates belong to the content box, not the decorative border.
      const width = Math.max(0, rect.width - (parseFloat(computedStyle.borderLeftWidth) || 0)
        - (parseFloat(computedStyle.borderRightWidth) || 0)
        - (parseFloat(computedStyle.paddingLeft) || 0) - (parseFloat(computedStyle.paddingRight) || 0));
      const height = Math.max(0, rect.height - (parseFloat(computedStyle.borderTopWidth) || 0)
        - (parseFloat(computedStyle.borderBottomWidth) || 0)
        - (parseFloat(computedStyle.paddingTop) || 0) - (parseFloat(computedStyle.paddingBottom) || 0));
      setMapSize(prev => {
        if (prev.width === width && prev.height === height) {
          return prev;
        }
        return { width, height };
      });
    };

    updateMapSize();
    const disconnect = observeElementResize(mapRef.current, updateMapSize);
    window.addEventListener('resize', updateMapSize);
    return () => {
      disconnect();
      window.removeEventListener('resize', updateMapSize);
    };
  }, [locations?.length, mapBaseHeight, mapBaseWidth]);

  if (!locations || locations.length === 0) {
    return null;
  }

  const scaleX = mapSize.width ? mapSize.width / mapBaseWidth : 1;
  const scaleY = mapSize.height ? mapSize.height / mapBaseHeight : 1;
  const scale = (scaleX > scaleY ? scaleX : scaleY);
  const investigatorScale = Math.min(scaleX, scaleY);
  const investigatorTokenSize = 60 * investigatorScale;
  const gateInvestigatorTokenSize = 30 * investigatorScale;
  const clueSize = 28 * investigatorScale;
  const clueOffset = -5 * investigatorScale;
  const gateSize = 100 * investigatorScale;
  const monsterSize = 50 * investigatorScale;

  const mapGeometryStyle = {
    '--map-unit': `${scale}px`,
    '--map-x-unit': `${scaleX}px`,
    '--map-token-unit': `${investigatorScale}px`,
    '--map-item-scale': investigatorScale * 0.5,
  } as React.CSSProperties;

  const getScaledPosition = (position?: LocationPosition | null) => {
    if (!position) {
      return null;
    }
    return {
      x: position.x * scaleX,
      y: position.y * scaleY,
    };
  };

  const handleLocationSelection = (
    event: React.MouseEvent<any, MouseEvent>,
    location: Location
  ) => {
    event.stopPropagation();
    setSelectedLocation(prev => (prev === location ? null : location));
  };

  const renderLocation = (location: Location) => {
    const gates = location.gates ?? [];
    const monsters = location.monsters ?? [];
    const investigatorNames = location.investigatorNames ?? [];
    const pos = positions[location.name];
    const scaledPos = getScaledPosition(pos);
    if (!pos || !scaledPos) return null;

    const isStreet = location.type === 'Street';
    const isSpecial = location.type === 'Special';
    const otherWorldMatch = location.name.match(/ (1|2)$/);
    const isOtherWorld = !!otherWorldMatch;
    const isLostInTime = location.name === 'Lost in Time and Space';
    const isOutskirts = location.name === 'Outskirts';
    const canDisplayRifts = location.type !== 'OtherWorld' && !isOtherWorld && !isLostInTime;
    const rifts = canDisplayRifts ? location.rifts ?? [] : [];
    const riftProgressTokens = riftProgressTokensByLocation[location.name] ?? [];
    const locationSpecificActions =
      locationActions?.filter(
        action =>
          (action.isLocationAction || action.locationName) &&
          (action.locationName === location.name || (typeof action.card === 'object' && !Array.isArray(action.card) && action.card?.name === location.name))
      ) || [];
    const otherWorldOrientation = otherWorldMatch?.[1] === '1' ? 'bottom' : 'top';
    const hasGate = !isOtherWorld && !isLostInTime && gates.length > 0;
    const gateDestinationInvestigatorNames = hasGate
      ? getGateDestinationInvestigatorNames(gates[0], otherWorldLookupLocations)
      : [];
    const hasActivity =
      (!!activeRumorLocationName && location.name === activeRumorLocationName) ||
      (!!activeMythosLocationName && location.name === activeMythosLocationName) ||
      (!!activeProcessLocationName && location.name === activeProcessLocationName);
    const hasHypnosVision = location.name === gameState.hypnosVisionLocation;
    const encounterTopCard = encounterTopCardByLocation[location.name];
    const hasEncounterTopCard = !!encounterTopCard?.name;
    const clues = location.cluesNumber || 0;
    const timeBombs = (location.placedCommonItems ?? []).filter(
      item => item.name === 'Time Bomb'
    );
    const timeBombWidth = 48 * investigatorScale;
    const timeBombHeight = timeBombWidth * (42 / 73);
    const timeBombClueSize = 18 * investigatorScale;
    const placedUniqueItems = location.placedUniqueItems ?? [];
    const facedownExhibitItemCount = location.facedownExhibitItemCount ?? 0;
    const showLocationTooltipButton =
      location.type !== 'OtherWorld' &&
      (
        locationCards(location, gameState.specialCardsInPlay).length > 0 ||
        hasEncounterTopCard ||
        (location.agentTokens ?? 0) > 0 ||
        location.hasExpedition ||
        gates.length > 0 ||
        monsters.length > 0 ||
        investigatorNames.length > 0 ||
        rifts.length > 0
      );
    const className = `location ${isStreet ? 'street' : 'location-circle'}${isSpecial ? ' special' : ''}${hasGate ? ' has-gate' : ''}${location.isStable ? ' stable' : ''}${isOtherWorld ? ' otherworld' : ''}${isOtherWorld ? ` otherworld-${otherWorldOrientation}` : ''}`;

    // Build gate tooltip if gate exists
    const gateTooltip = hasGate
      ? gates.map(getGateTooltip).join('\n---\n')
      : '';
    
    const locationHasAction = locationSpecificActions.length > 0 && onExecuteAction;
    const locationActionDisabled = !!loading || !!locationSpecificActions[0]?.disabled;
    const locationActionEnabled = locationHasAction && !locationActionDisabled;
    const locationSize = 85;
    const color = locationActionEnabled ? locationSpecificActions[0].color || '#cd37e1' :
      location.isSealed ? '#3ff3e275' : '#888888';
    const locationShadowBoxTemplate = `0 0 ${12 * scale}px ${12 * scale}px ${color}, 0 0 ${18 * scale}px ${color}`;
    const boxShadow = !locationActionEnabled && !location.isSealed ? 'none' : locationShadowBoxTemplate;

    return (
      <div
        key={location.name}
        className={`${className} ${location.name} ${locationActionEnabled ? 'location-with-action' : ''}${locationHasAction && locationActionDisabled ? ' location-action-disabled' : ''}`}
        aria-disabled={locationHasAction ? locationActionDisabled : undefined}
        style={{
          left: `${scaledPos.x}px`,
          top: `${scaledPos.y}px`,
          width: `${(pos.width || locationSize) * scaleX}px`,
          height: `${(pos.height || locationSize) * scaleX}px`,
          boxShadow: boxShadow,
        }}
        title={`${location.name} (${location.district})`}
        onClick={(e) => {
          if (!locationActionEnabled) {
            return;
          }

          e.stopPropagation();
          onExecuteAction(locationSpecificActions[0].id)
        }}
      >
        {showLocationTooltipButton && (<button
          type="button"
          className={`tooltip-button ${location.name === selectedLocation?.name ? 'active' : ''} location-tooltip${hasEncounterTopCard ? ' location-tooltip-encounter' : ''}`}
          style={{
            width: `${28 * scale}px`,
            height: `${28 * scale}px`,
            fontSize: 16 * scale,
            left: `-${14 * scale}px`,
            top: `-${14 * scale}px`
          }}
          onClick={(e) => handleLocationSelection(e, location)}
        >
          {hasEncounterTopCard ? '!' : '?'}
        </button>)}
        {isOutskirts && (
          <div
            className="outskirts-info"
            style={{
              left: 0,
              top: 0,
              fontSize: 6 * scaleX,
              padding: `${6 * scaleX}px ${10 * scaleX}px`
            }}
          >
            {outskirtsMonsters} / {outskirtsMax || '—'}
          </div>
        )}
        <div className="location-inner">
          {timeBombs.length > 0 && (
            <div
              className="time-bomb-markers-location"
              style={{
                right: `calc(100% + ${4 * investigatorScale}px)`,
                gap: `${6 * investigatorScale}px`,
              }}
            >
              {timeBombs.map((timeBomb, index) => (
                <div
                  className="time-bomb-marker"
                  key={`${timeBomb.name}-${index}`}
                  style={{
                    width: `${timeBombWidth}px`,
                    height: `${timeBombHeight}px`,
                  }}
                >
                  <img
                    loading="lazy"
                    src="/TimeBombToken.jpg"
                    alt="Time Bomb token"
                    className="time-bomb-token-image"
                  />
                  <div
                    className="time-bomb-clue-badge"
                    style={{
                      width: `${timeBombClueSize}px`,
                      height: `${timeBombClueSize}px`,
                      top: `${-4 * investigatorScale}px`,
                      right: `${-4 * investigatorScale}px`,
                    }}
                  >
                    <ArtworkImage
                      loading="lazy"
                      src="/Clue.jpg"
                      alt=""
                      className="time-bomb-clue-image"
                    />
                    <span
                      className="time-bomb-clue-count"
                      style={{ fontSize: `${12 * investigatorScale}px` }}
                    >
                      {timeBomb.usesRemaining ?? 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {(placedUniqueItems.length > 0 || facedownExhibitItemCount > 0) && (
            <div
              className="placed-unique-items-location"
              style={{
                left: `calc(100% + ${4 * investigatorScale}px)`,
                gap: `${4 * investigatorScale}px`,
              }}
            >
              <StackedItems
                kind="unique"
                title=""
                items={placedUniqueItems}
                additionalUnusedItems={Array.from(
                  { length: facedownExhibitItemCount },
                  (_, index) => (
                    <div
                      className="stacked-card"
                      key={`facedown-exhibit-${index}`}
                      title="Facedown Exhibit Item"
                      style={{ zIndex: placedUniqueItems.length + index }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className={`card${artworkStyle === 'Digital' ? ' digital-card-back' : ''}`}>
                        <img
                          loading="lazy"
                          src={getCardBackPath('exhibits', artworkStyle)}
                          alt="Facedown Exhibit Item"
                          className="placed-unique-item-image"
                        />
                      </div>
                    </div>
                  )
                )}
              />
            </div>
          )}

          {hasHypnosVision && (
            <img
              loading="lazy"
              src="/Guardians/HypnosToken.jpg"
              alt="Hypnos vision token"
              title="Hypnos vision"
              className="hypnos-vision-token"
              style={{
                width: `${32 * investigatorScale}px`,
                height: `${32 * investigatorScale}px`,
              }}
            />
          )}

          {hasActivity && (
            <ArtworkImage loading="lazy"
              src="/activity.png"
              alt="Rumor activity"
              className="activity-marker"
              style={{
                width: `${28 * scale}px`,
                height: `${28 * scale}px`,
              }}
            />
          )}

          {!!location.hasAncientWhisperMarker && (
            <ArtworkImage alt="Ancient Whisper Marker" src="/AncientWhisper.jpg" style={{
              position: 'absolute',
              left: 0,
              width: `${60 * investigatorScale}px`,
              height: `${60 * investigatorScale}px`,
            }} />
          )}

          {!!location.hasPatrol && (
            <ArtworkImage alt="Patrol Marker" src="/patrol.png" style={{
              position: 'absolute',
              right: 0,
              width: `${60 * investigatorScale}px`,
              height: `${60 * investigatorScale}px`,
            }} />
          )}

          {!!location.agentTokens && <div style={{ position: 'absolute', left: 0, top: 0 }}><AgentBadge count={location.agentTokens} /></div>}
          {location.hasExpedition && <div style={{ position: 'absolute', left: 30, top: 0 }}><ExpeditionBadge /></div>}

          {hasGate && (
            <div
              className='gate-icon gate-hover'
              style={{
                width: `${gateSize}px`,
                height: `${gateSize}px`,
                borderRadius: '50%',
                boxShadow: locationHasAction ? `0 0 ${1 * scale}px ${1 * scale}px rgba(255, 0, 0, 0.9), 0 0 ${1 * scale}px rgba(255, 0, 0, 0.8)` : `0 0 ${4 * scale}px ${6 * scale}px rgba(255, 0, 0, 0.9), 0 0 ${6 * scale}px rgba(255, 0, 0, 0.8)`
            }}>
              <GateMarker
                gate={gates[0]}
                className='gate-icon gate-hover'
                imageClassName='gate-icon'
                title={gateTooltip}
                language={language}
                interactiveTooltip
                actions={locationSpecificActions}
                loading={loading}
                onExecuteAction={onExecuteAction}
              />
            </div>
          )}

          {hasGate && gateDestinationInvestigatorNames.length > 0 && (
            <div
              className="gate-destination-investigators"
              style={{
                bottom: `calc(50% + ${gateSize * 0.42}px)`,
                gap: `${2 * investigatorScale}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {gateDestinationInvestigatorNames.map((name, index) => {
                const markerInvestigator = investigatorsByName?.[name] ?? gameState.investigatorsByName?.[name];
                if (!markerInvestigator) {
                  return null;
                }

                return (
                  <InvestigatorMarker
                    key={`${gates[0].otherWorld}-${name}-${index}`}
                    investigator={{
                      name,
                      delayed: markerInvestigator.delayed ?? false,
                    }}
                    onClick={() => setOpenInvestigatorName(markerInvestigator.name)}
                    idx={index}
                    scale={investigatorScale}
                    highlighted={!!currentInvestigator ? currentInvestigator.name === name : highlightedInvestigators.includes(name)}
                    size={gateInvestigatorTokenSize}
                    offsetByIndex={false}
                  />
                );
              })}
            </div>
          )}

          {rifts.length > 0 && (
            <div className="rift-markers-location">
              {rifts.map((rift, index) => (
                <RiftMarker
                  key={`${rift.id}-${rift.dimensionalSign}-${index}`}
                  rift={rift}
                  size={90 * investigatorScale}
                />
              ))}
            </div>
          )}

          {riftProgressTokens.length > 0 && (
            <div
              className="rift-progress-tokens-location"
              style={{
                gap: `${2 * investigatorScale}px`,
              }}
            >
              {riftProgressTokens.map(token => (
                <img
                  key={`${token.riftProgressEntry.id}-${token.locationName}-${token.occurrenceIndex}`}
                  loading="lazy"
                  src={`/rifts/${token.locationName}.png`}
                  alt={`${token.locationName} rift progress token`}
                  title={`${token.locationName} rift progress token`}
                  className={`rift-progress-token-location${token.isOpen ? ' open' : ''}`}
                  style={{
                    width: `${24 * investigatorScale}px`,
                    height: `${24 * investigatorScale}px`,
                  }}
                />
              ))}
            </div>
          )}

          {clues > 0 && (
            <div
              className="clue-marker-wrapper"
              style={{
                width: `${clueSize}px`,
                height: `${clueSize}px`,
                bottom: `${clueOffset}px`,
                marginLeft: `${(clues <= 5 && clues > 1) ? `-${clues * (clueSize * 0.3)}px` : undefined}`,
              }}
            >
              {clues <= 5 ? (
                <div>
                  {Array.from({ length: clues }).map((_, idx) => (
                    <div
                      className="clue-marker"
                      key={idx}
                      style={{
                        width: `${clueSize}px`,
                        height: `${clueSize}px`,
                        left: `${idx * (clueSize * 0.4)}px`,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="clue-marker"
                  style={{
                    width: `${clueSize}px`,
                    height: `${clueSize}px`,
                  }}
                >
                  <span className="clue-count" style={{ width: `${clueSize * 0.64}px`, height: `${clueSize * 0.64}px` }}>
                    {clues}
                  </span>
                </div>
              )}
            </div>
          )}

          {investigatorNames.length > 0 && (
            <div className="investigators-at-location" style={{
              width: `${investigatorTokenSize}px`,
              height: `${investigatorTokenSize}px`,
              transform: `translateX(-${(investigatorNames.length - 1) * 10 * investigatorScale}px)`,
            }}>
              {investigatorNames.map((name, index) => {
                const markerInvestigator = investigatorsByName?.[name];
                if (!markerInvestigator) {
                  return null;
                }
                return (
                  <InvestigatorMarker
                    key={`${name}-${index}`}
                    investigator={{
                      name,
                      delayed: markerInvestigator?.delayed ?? false,
                    }}
                    onClick={() => setOpenInvestigatorName(markerInvestigator.name)}
                    idx={index}
                    scale={investigatorScale}
                    highlighted={!!currentInvestigator ? currentInvestigator?.name === name : highlightedInvestigators.includes(name)}
                    size={investigatorTokenSize}
                  />
                );
              })}
            </div>
          )}

          {location.isSealed && <div className="sealed-marker" style={{
            width: `${90 * scale}px`,
            height: `${90 * scale}px`
          }} />}

          {location.isClosed && <div className="closed-marker" style={{
            width: `${25 * scale}px`,
            height: `${40 * scale}px`
          }} />}
        </div>
        {!isOtherWorld && !isLostInTime && monsters.length > 0 && (
          <Monsters monsters={monsters} showInfo={false} wrapperClass="monster-markers-location" wrapperStyle={{ height: `${monsterSize}px` }} monsterSize={monsterSize} />
        )}
      </div>
    );
  };

  const terrorScaledPosition = getScaledPosition({
    x: TERROR_POSITIONS[terrorLevel].left,
    y: TERROR_POSITIONS[terrorLevel].bottom,
  }) || { x: 0, y: 0 };

  const TerrorTrack = ({id} : {id: 1 | 2 | 3}) => {
    const dunwichTerrorScaledPosition = getScaledPosition({
      x: DUNWICH_TERROR_POSITIONS[id].left,
      y: DUNWICH_TERROR_POSITIONS[id].top,
    }) || { x: 0, y: 0 };

    return showDunwichTerrorTrack && dunwichHorrorTerrorTrack >= id ? (
      <div className="dunwich-terror" style={{
        top: `${dunwichTerrorScaledPosition.y}px`,
        left: `${dunwichTerrorScaledPosition.x}px`,
        width: `${50 * scaleX}px`,
        height: `${30 * scaleY}px`
      }} />
    ) : null;
  }

  const DunwichTerrorTrackTooltipButton = () => {
    if (!showDunwichTerrorTrack) {
      return null;
    }

    const dunwichTerrorScaledPosition = getScaledPosition({
      x: DUNWICH_TERROR_POSITIONS[3].left,
      y: DUNWICH_TERROR_POSITIONS[3].top,
    });

    if (!dunwichTerrorScaledPosition) {
      return null;
    }

    return (
      <div>
        <button
          type="button"
          className="tooltip-button location-tooltip"
          style={{
            width: `${28 * scale}px`,
            height: `${28 * scale}px`,
            fontSize: 16 * scale,
            left: `${dunwichTerrorScaledPosition.x + (65 * scale)}px`,
            top: `${dunwichTerrorScaledPosition.y - (30 * scale)}px`,
            minHeight: `${28 * scale}px`,
            minWidth: `${28 * scale}px`,
          }}
          onClick={() => setDunwichTerrorTooltipDisplayed(!dunwichTerrorTooltipDisplayed)}
        >
          ?
        </button>
      </div>
    );
  }

  const selectedLocationTopEncounterCard =
    selectedLocation
      ? encounterTopCardByLocation[selectedLocation.name]
      : undefined;

  return (
    <div className="map-container">
      {openInvestigator && <InvestigatorInspector state={gameState} investigator={openInvestigator}
        investigators={Object.values(investigatorsByName ?? {})} viewerName={viewerName} selectedInvestigatorName={currentInvestigator?.name}
        actions={gameState.availableActions ?? locationActions ?? []} loading={loading} language={language} onExecuteAction={onExecuteAction} onClose={() => setOpenInvestigatorName(null)} />}
      {dunwichTerrorTooltipDisplayed && (
        <GameDialog title="Dunwich Horror cards" onClose={() => setDunwichTerrorTooltipDisplayed(false)}>
            <div className="dunwich-terror-popup-grid">
              {Array.from({ length: gameState.gameSettings?.includeDunwichCards && gameState.gameSettings?.includeMiskatonicCards ? 13 : DUNWICH_HORROR_CARD_COUNT }).map((_, index) => {
                const cardNumber = index + 1;
                return (
                  <img
                    key={cardNumber}
                    loading="lazy"
                    alt={`Dunwich Horror option ${cardNumber}`}
                    src={`/Monsters/TheDunwichHorrorCard${cardNumber}.png`}
                  />
                );
              })}
            </div>
        </GameDialog>
      )}
      <div
        ref={mapRef}
        className="map"
        style={{
          ...mapGeometryStyle,
          aspectRatio: mapAspectRatio,
          backgroundImage: `url('${mapBackgroundImage}')`,
        }}
      >
        {!!deepOnesRisingTrack && (
          <div className='deep-one-rising-track-container'>
            {Array.from({ length: deepOnesRisingTrack }).map((_, index) => {
              return (
                <img
                  className='deep-one-rising-track'
                  style={
                    {
                      top: DEEP_ONE_RAISING_TRACK_POSITIONS[index].top * scaleY,
                      right: DEEP_ONE_RAISING_TRACK_POSITIONS[index].right * scaleX,
                      height: `${48 * scale}px`,
                      width: `${48 * scale}px`
                    }
                  }
                  key={`deep_rising_one_${index}`}
                  loading='lazy'
                  alt='deep one raising track token'
                  src={'/deepOneRisingToken.jpg'}
                />
              );
            })}
          </div>
        )}
        {!!fedsRaidInnsmouthTrack && (
          <div className='deep-one-rising-track-container'>
            {Array.from({ length: fedsRaidInnsmouthTrack.churchGreen }).map((_, index) => {
              return (
                <ArtworkImage
                  className='fed-red-track'
                  style={
                    {
                      top: (index === 0 ? 353 : 405) * scaleY,
                      right: (index === 0 ? 138 : 127) * scaleX,
                      height: `${48 * scale}px`,
                      width: `${48 * scale}px`
                    }
                  }
                  key={`fed_raids_church_${index}`}
                  loading='lazy'
                  alt='fed raid track token'
                  src={'/Clue.jpg'}
                />
              );
            })}
            {Array.from({ length: fedsRaidInnsmouthTrack.factoryDistrict }).map((_, index) => {
              return (
                <ArtworkImage
                  className='fed-red-track'
                  style={
                    {
                      top: (index === 0 ? 505 : 458) * scaleY,
                      right: (index === 0 ? 83 : 108) * scaleX,
                      height: `${48 * scale}px`,
                      width: `${48 * scale}px`
                    }
                  }
                  key={`fed_raids_factory_${index}`}
                  loading='lazy'
                  alt='fed raid track token'
                  src={'/Clue.jpg'}
                />
              );
            })}
            {Array.from({ length: fedsRaidInnsmouthTrack.innsmouthShore }).map((_, index) => {
              return (
                <ArtworkImage
                  className='fed-red-track'
                  style={
                    {
                      top: (index === 0 ? 585 : 549) * scaleY,
                      right: (index === 0 ? 12 : 49) * scaleX,
                      height: `${48 * scale}px`,
                      width: `${48 * scale}px`
                    }
                  }
                  key={`fed_raids_shore_${index}`}
                  loading='lazy'
                  alt='fed raid track token'
                  src={'/Clue.jpg'}
                />
              );
            })}
          </div>
        )}
        {showMapStats && (
          <div className="map-stats" style={{
            fontSize: 6 * scaleX,
            padding: `${6 * scaleX}px ${10 * scaleX}px`
          }}>
          <div>Monsters: {monstersInArkhamCount}{(!!spawnMonstersInArkhamCount && spawnMonstersInArkhamCount.length > 0) ? ` (+${spawnMonstersInArkhamCount.length})` : ""} / {monsterLimit || '—'}</div>
          <div>Gates: {gatesOpened} / {maximumGates || '—'}</div>
            <div>Seals: {seals} / 6</div>
          </div>
        )}

        {/* Render all locations */}
        {locations.map(location => renderLocation(location))}

        {showTerrorTrack && (
          <div className="terror" style={{
            bottom: `${terrorScaledPosition.y}px`,
            left: `${terrorScaledPosition.x}px`,
            boxShadow: `0 0 ${2 * scale}px ${5 * scale}px red`,
            width: `${30 * scaleX}px`,
            height: `${47 * scaleY}px`
          }} />
        )}

        <TerrorTrack id={1} />
        <TerrorTrack id={2} />
        <TerrorTrack id={3} />

        {showDunwichTerrorTrack && <DunwichTerrorTrackTooltipButton />}

        {riftProgress !== undefined && <RiftProgress riftProgress={riftProgress} scale={scaleX} />}

        {selectedLocation && (
          <ClassicLocationDialog title={`${selectedLocation.name} details`} onClose={() => setSelectedLocation(null)}>
            <div className="location-detail-header">
              <div>
                <div className="location-detail-name">{selectedLocation.name}</div>
                <div className="location-detail-district">{selectedLocation.district}</div>
              </div>
              <div className="location-detail-tags">
                <AgentBadge count={selectedLocation.agentTokens} />
                {selectedLocation.hasExpedition && <ExpeditionBadge />}
                {selectedLocation.isStable && <span className="location-tag stable">Stable</span>}
                {selectedLocation.isSealed && <span className="location-tag sealed">Sealed</span>}
                {selectedLocation.isClosed && <span className="location-tag closed">Closed</span>}
              </div>
            </div>

            <div className="location-detail-grid full">
              <LocationCardsSection location={selectedLocation} specialCardsInPlay={gameState.specialCardsInPlay} />
              {selectedLocation.hasAncientWhisperMarker && <AncientWhisperInfo />}
              {selectedLocationTopEncounterCard?.name ? (
                <div className="detail-section">
                  <div className="detail-title">Encounter</div>
                  <div className="detail-text">{selectedLocationTopEncounterCard.name}</div>
                </div>
              ) : null}

              {selectedLocation.monsters?.length ? (
                <div className="detail-section">
                  <div className="detail-title">Monsters</div>
                  <LocationMonstersSection monsters={selectedLocation.monsters} />
                </div>
              ) : null}

              {selectedLocation.gates?.length ? (
                <div className="detail-section">
                  <div className="detail-title">Gates</div>
                  <div className="detail-gates">
                    {selectedLocation.gates.map((gate, idx) => (
                      <div key={`${gate.otherWorld}-${idx}`} className="gate-detail-image-wrapper">
                        <span title={getGateTooltip(gate)}><GateArtwork gate={gate}
                          classicPath={getGateImagePath(gate, language)}
                          alt={`${getGateDestinations(gate).join(' / ')} gate`}
                          className="gate-detail-image gate-hover"
                        /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedLocation.rifts?.length ? (
                <div className="detail-section">
                  <div className="detail-title">Rifts</div>
                  <div className="detail-rifts">
                    {selectedLocation.rifts.map((rift, idx) => (
                      <RiftMarker
                        key={`${rift.id}-${rift.dimensionalSign}-${idx}`}
                        rift={rift}
                        className="rift-marker-detail"
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {selectedLocation.investigatorNames?.length ? (
                <div className="detail-section full-width">
                  <div className="detail-title">Investigators</div>
                  <div className="detail-investigator-sheets">
                    {selectedLocation.investigatorNames.map((name) => {
                      const investigator = investigatorsByName?.[name];
                      if (!investigator) {
                        return (
                          <div key={name} className="detail-investigator-fallback">
                            {name}
                          </div>
                        );
                      }

                      return (
                        <div key={name} className="detail-investigator-sheet">
                          <PlayerHeader gameState={gameState} investigator={investigator} />
                          <section className="game-investigator-sheet-scroll" tabIndex={0} aria-label={`${name} investigator sheet`}
                            onKeyDown={event => {
                              if (event.target === event.currentTarget && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                                event.preventDefault();
                                event.currentTarget.scrollBy({ left: event.key === 'ArrowRight' ? 60 : -60 });
                              }
                            }}>
                            <InvestigatorDisplay gameState={gameState}
                              investigator={investigator}
                              deckInfo={deckInfo}
                              showStats
                              language={language}
                              availableActions={(gameState?.availableActions || []).map(action => ({
                                ...action, disabled: !!loading || !!action.disabled,
                              }))}
                              onExecuteAction={onExecuteAction}
                            />
                          </section>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
            {selectedLocation.hasAncientWhisperMarker && <ExhibitEncounterCatalog language={language === 'Ru' ? 'Ru' : 'En'} gameState={gameState} />}
          </ClassicLocationDialog>
        )}
      </div>
    </div>
  );
});
